import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { ScienceContent } from "@/components/ScienceContent";
import { SubjectPill } from "@/lib/accents";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, ArrowLeft, HelpCircle, X } from "lucide-react";
import { toast } from "sonner";

const RATINGS = [
  { key: "again", label: "Again", hint: "I couldn't remember it", num: 1, cls: "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 focus-visible:ring-rose-500" },
  { key: "hard", label: "Hard", hint: "Remembered, but difficult", num: 2, cls: "bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200 focus-visible:ring-amber-500" },
  { key: "good", label: "Good", hint: "Remembered correctly", num: 3, cls: "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200 focus-visible:ring-emerald-500" },
  { key: "easy", label: "Easy", hint: "Knew it immediately", num: 4, cls: "bg-sky-50 hover:bg-sky-100 text-sky-800 border-sky-200 focus-visible:ring-sky-500" },
];

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now());

export default function Study() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const rawMode = params.get("mode");
  const mode = rawMode === "practice" ? "practice" : rawMode === "weak" ? "weak" : "daily";
  const courseId = params.get("course");
  const topicId = params.get("topic");
  const deckId = params.get("deck");

  const [sessionId, setSessionId] = useState(null);
  const [sessionMode, setSessionMode] = useState(mode);
  const [card, setCard] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [intervals, setIntervals] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("ss-hide-help") !== "1");
  const eventIdRef = useRef(uid());

  const fetchNext = useCallback(async (sid) => {
    setRevealed(false);
    setAnswer(null);
    setIntervals(null);
    const { data } = await api.get(`/study/session/${sid}/next`);
    if (data.done) {
      setSummary(data);
      setCard(null);
    } else {
      eventIdRef.current = uid();
      setCard(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const body = mode === "practice"
          ? { mode, course_id: courseId, topic_id: topicId, deck_id: deckId }
          : mode === "weak"
          ? { mode, course_id: courseId }
          : { mode: "daily", course_id: courseId, topic_id: topicId, deck_id: deckId };
        const { data } = await api.post("/study/session", body);
        if (!active) return;
        setSessionId(data.session_id);
        setSessionMode(data.mode);
        if (data.mode === "practice" && mode === "daily" && data.total > 0) {
          toast.success("You've learned everything here for now, so here's some extra practice. It won't change your review schedule.");
        }
        if (data.total === 0) {
          setSummary({ done: true, reviewed: 0, remembered: 0, needs_practice: 0, duration_min: 0, next_due_tomorrow: 0, empty: true, mode });
          setLoading(false);
        } else {
          await fetchNext(data.session_id);
        }
      } catch (e) {
        toast.error(apiErr(e));
        navigate("/flashcards/dashboard");
      }
    })();
    return () => { active = false; };
  }, [mode, courseId, topicId, deckId, fetchNext, navigate]);

  const reveal = useCallback(async () => {
    if (!card || revealed) return;
    try {
      const { data } = await api.post(`/study/session/${sessionId}/reveal`, { card_id: card.card_id });
      setAnswer(data);
      setIntervals(data.intervals);
      setRevealed(true);
    } catch (e) { toast.error(apiErr(e)); }
  }, [card, revealed, sessionId]);

  const rate = useCallback(async (rating) => {
    if (!card || !revealed || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/study/session/${sessionId}/review`, {
        card_id: card.card_id, rating, event_id: eventIdRef.current,
      });
      await fetchNext(sessionId);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  }, [card, revealed, submitting, sessionId, fetchNext]);

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (!card) return;
      if (!revealed && (e.key === " " || e.key === "Enter")) { e.preventDefault(); reveal(); return; }
      if (revealed && !submitting) {
        const map = { "1": "again", "2": "hard", "3": "good", "4": "easy", " ": "good" };
        if (map[e.key]) { e.preventDefault(); rate(map[e.key]); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, revealed, submitting, reveal, rate]);

  const dismissHelp = () => { setShowHelp(false); localStorage.setItem("ss-hide-help", "1"); };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;

  if (summary) {
    const { reviewed, remembered, needs_practice, duration_min, next_due_tomorrow, empty } = summary;
    return (
      <div className="max-w-lg mx-auto text-center py-10 ss-fade-up" data-testid="session-complete">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
        <h1 className="mt-4 font-display text-3xl font-bold text-slate-900">
          {empty ? "Nothing due right now" : "You're done for today"}
        </h1>
        {empty ? (
          <p className="mt-2 text-slate-500">You're up to date. Your next review will be ready soon.</p>
        ) : (
          <>
            <p className="mt-2 text-slate-500">You reviewed {reviewed} card{reviewed === 1 ? "" : "s"}.</p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-left">
              <Stat label="Remembered" value={remembered} tone="emerald" />
              <Stat label="Need more practice" value={needs_practice} tone="amber" />
              <Stat label="Time studied" value={`${duration_min} min`} />
              <Stat label="Due tomorrow" value={next_due_tomorrow} />
            </div>
          </>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {sessionMode === "daily" && (
            <Button onClick={() => navigate("/flashcards/courses")} data-testid="revise-more-button"
              className="bg-[#FFC200] text-[#141821] hover:bg-[#141821] hover:text-[#FFC200] h-11 px-8 rounded-full font-semibold transition-colors">
              Revise more / Extra Practice
            </Button>
          )}
          <Button onClick={() => navigate("/flashcards/dashboard")} data-testid="back-to-dashboard-button"
            variant={sessionMode === "daily" ? "outline" : "default"}
            className={sessionMode === "daily" ? "h-11 px-8" : "bg-slate-900 hover:bg-slate-800 h-11 px-8"}>Back to dashboard</Button>
        </div>
      </div>
    );
  }

  const pct = card.total ? Math.round(((card.index - 1) / card.total) * 100) : 0;

  return (
    <div className="max-w-2xl mx-auto flex flex-col min-h-[78vh]">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => navigate("/flashcards/dashboard")} className="text-slate-400 hover:text-slate-700" data-testid="exit-study-button">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <Progress value={pct} className="h-1.5" />
        </div>
        <span className="text-sm text-slate-500 tabular-nums" data-testid="study-progress">{card.index} / {card.total}</span>
      </div>

      {sessionMode === "practice" && (
        <div className="mt-3 self-center rounded-full bg-slate-100 text-slate-600 text-xs font-mono uppercase tracking-wide px-3 py-1" data-testid="extra-practice-badge">Extra Practice</div>
      )}
      {sessionMode === "weak" && (
        <div className="mt-3 self-center rounded-full bg-rose-50 text-rose-700 text-xs font-mono uppercase tracking-wide px-3 py-1">Weak spots · practice</div>
      )}

      <div className="mt-4 flex items-center justify-center gap-2 text-center">
        <SubjectPill subject={card.subject}>{card.course_name}</SubjectPill>
      </div>
      <p className="mt-1 text-center text-sm text-slate-400 font-mono">{card.topic_name}</p>

      <div className="mt-4 flex-1 flex flex-col">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 min-h-[180px] flex items-center justify-center relative overflow-hidden">
          {card.watermark && (
            <span className="pointer-events-none select-none absolute inset-0 flex items-center justify-center text-slate-100 text-xl font-bold rotate-[-20deg] opacity-60">{card.watermark}</span>
          )}
          <div className="relative w-full">
            <ScienceContent text={card.question} images={card.images} className="text-lg sm:text-xl text-slate-900 text-center" />
          </div>
        </div>

        {revealed && answer && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-6 sm:p-8 ss-fade-up" data-testid="answer-panel">
            <ScienceContent text={answer.answer} images={answer.images} className="text-slate-800" />
            {answer.explanation && (
              <div className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-500">
                <ScienceContent text={answer.explanation} />
              </div>
            )}
          </div>
        )}
      </div>

      {showHelp && !revealed && (
        <div className="mt-4 rounded-lg bg-slate-900 text-slate-100 p-4 text-sm relative" data-testid="study-help">
          <button onClick={dismissHelp} className="absolute top-2 right-2 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
          <div className="flex items-start gap-2 pr-6">
            <HelpCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>Read the question, then reveal the answer and rate yourself honestly. <strong>Again</strong> means you couldn't recall it — don't press Hard if you actually forgot.</p>
          </div>
        </div>
      )}

      <div className="sticky bottom-16 md:bottom-4 mt-6 pt-3">
        {!revealed ? (
          <Button onClick={reveal} data-testid="show-answer-button"
            className="w-full h-14 text-base bg-slate-900 hover:bg-slate-800">Show answer</Button>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rating-buttons">
            {RATINGS.map((r) => (
              <button key={r.key} onClick={() => rate(r.key)} disabled={submitting}
                data-testid={`rating-${r.key}-button`}
                className={`flex flex-col items-center justify-center rounded-xl border h-16 sm:h-20 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 ${r.cls}`}>
                <span className="text-base">{r.label}</span>
                <span className="text-[11px] opacity-70 font-mono">
                  {intervals?.[r.key] ? intervals[r.key] : `${r.num}`}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === "emerald" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`font-display text-2xl font-bold ${toneCls}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
